import { useAuth } from '../store/AuthContext';
import { Link, Navigate } from 'react-router-dom';
import { PRODUCTS, translateSupplier, type Product } from '../store/productData';
import { useState } from 'react';
import { useLanguage } from '../store/LanguageContext';
import { getQuotesForUser, deleteQuote, generateQuotePDF, type SavedQuote } from '../store/quoteStore';
import SEOHead from '../components/SEOHead';
import { FileText, Factory, DollarSign, TrendingUp, Package, Tag, Clock, Heart, Calculator, Construction, FileDown, Trash2, Plus, X, Upload } from 'lucide-react';

interface ManagedProduct extends Product {
  serialNumber?: string;
  stock?: number;
}

function ProductFormModal({ product, onSave, onClose, t }: {
  product: ManagedProduct | null;
  onSave: (p: ManagedProduct) => void;
  onClose: () => void;
  t: (key: string) => string;
}) {
  const isEdit = !!product;
  const [form, setForm] = useState<ManagedProduct>(product || {
    id: `p_${Date.now()}`,
    name: '',
    slug: '',
    color: 'Rood',
    colorHex: '#a0522d',
    texture: 'Ruw',
    pricePerM2: 0,
    wholesalePrice: { 1: 0, 2: 0, 3: 0 },
    supplierCode: '',
    supplier: 'Fabrikant Polen',
    images: [],
    description: '',
    dimensions: '240 x 71 x 15 mm',
    weight: '4.0 kg/m²',
    category: 'klassiek',
    serialNumber: '',
    stock: 0,
  });

  const handleChange = (field: string, value: string | number) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleWholesaleChange = (tier: number, value: number) => {
    setForm(prev => ({ ...prev, wholesalePrice: { ...prev.wholesalePrice, [tier]: value } }));
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
      <div className="relative bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="sticky top-0 bg-white border-b border-gray-100 px-6 py-4 flex items-center justify-between rounded-t-2xl z-10">
          <h3 className="text-lg font-bold">{isEdit ? t('dash.editProduct') : t('dash.addProduct')}</h3>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-gray-100"><X size={18} /></button>
        </div>
        <div className="p-6 space-y-5">
          {/* Basic Info */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.productName')}</label>
              <input type="text" value={form.name} onChange={e => handleChange('name', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
                placeholder="Roma Classico" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.serialNumber')}</label>
              <input type="text" value={form.serialNumber || ''} onChange={e => handleChange('serialNumber', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
                placeholder="PL-RC-001" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.supplierCode')}</label>
              <input type="text" value={form.supplierCode} onChange={e => handleChange('supplierCode', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
                placeholder="PL-RC-001" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.supplier')}</label>
              <select value={form.supplier} onChange={e => handleChange('supplier', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]">
                <option value="Fabrikant Polen">🇵🇱 Fabrikant Polen</option>
                <option value="Fabrikant Spanje">🇪🇸 Fabrikant Spanje</option>
                <option value="Fabrikant Italië">🇮🇹 Fabrikant Italië</option>
              </select>
            </div>
          </div>

          {/* Color & Texture */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.color')}</label>
              <select value={form.color} onChange={e => handleChange('color', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]">
                {['Rood', 'Wit', 'Bruin', 'Zwart', 'Geel', 'Grijs'].map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.texture')}</label>
              <select value={form.texture} onChange={e => handleChange('texture', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]">
                {['Ruw', 'Glad', 'Gesinterd', 'Gewassen'].map(tx => (
                  <option key={tx} value={tx}>{tx}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.category')}</label>
              <select value={form.category} onChange={e => handleChange('category', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]">
                {['klassiek', 'modern', 'landelijk', 'industrieel'].map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>
          </div>

          {/* Pricing */}
          <div>
            <h4 className="text-sm font-bold mb-3 flex items-center gap-2">
              <DollarSign size={14} className="text-[#c47a3a]" /> {t('dash.pricing')}
            </h4>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <label className="text-[10px] font-medium text-[#8a7e74] mb-1 block">{t('dash.retailPrice')}</label>
                <div className="relative">
                  <span className="absolute left-3 top-2 text-xs text-[#8a7e74]">€</span>
                  <input type="number" value={form.pricePerM2} onChange={e => handleChange('pricePerM2', +e.target.value)}
                    className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                </div>
              </div>
              {[1, 2, 3].map(tier => (
                <div key={tier}>
                  <label className="text-[10px] font-medium text-[#8a7e74] mb-1 block">B2B Tier {tier}</label>
                  <div className="relative">
                    <span className="absolute left-3 top-2 text-xs text-[#8a7e74]">€</span>
                    <input type="number" value={form.wholesalePrice[tier] || 0} onChange={e => handleWholesaleChange(tier, +e.target.value)}
                      className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Dimensions & Stock */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.dimensions')}</label>
              <input type="text" value={form.dimensions} onChange={e => handleChange('dimensions', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
                placeholder="240 x 71 x 15 mm" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.weight')}</label>
              <input type="text" value={form.weight} onChange={e => handleChange('weight', e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
                placeholder="4.0 kg/m²" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.stock')}</label>
              <input type="number" value={form.stock || 0} onChange={e => handleChange('stock', +e.target.value)}
                className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a]"
                placeholder="0" />
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-medium text-[#8a7e74] mb-1 block">{t('dash.description')}</label>
            <textarea value={form.description} onChange={e => handleChange('description', e.target.value)} rows={3}
              className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-[#c47a3a] resize-none"
              placeholder={t('dash.descriptionPlaceholder')} />
          </div>
        </div>

        <div className="sticky bottom-0 bg-white border-t border-gray-100 px-6 py-4 flex justify-end gap-3 rounded-b-2xl">
          <button onClick={onClose} className="px-5 py-2.5 text-sm font-medium text-[#8a7e74] hover:text-[#1a1a1a] transition-colors">
            {t('dash.cancel')}
          </button>
          <button
            onClick={() => {
              const slug = form.name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
              onSave({ ...form, slug, supplierCode: form.serialNumber || form.supplierCode });
            }}
            className="btn-primary !py-2.5 !px-6 text-sm"
          >
            {isEdit ? t('dash.saveChanges') : t('dash.addProduct')}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const { user, isAdmin, isWholesaler } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const { t } = useLanguage();
  const [quotesRefresh, setQuotesRefresh] = useState(0);
  const [managedProducts, setManagedProducts] = useState<ManagedProduct[]>(() => {
    try {
      const saved = localStorage.getItem('gevel_managed_products');
      return saved ? JSON.parse(saved) : PRODUCTS.map(p => ({ ...p, serialNumber: p.supplierCode, stock: Math.floor(Math.random() * 500) + 50 }));
    } catch { return PRODUCTS.map(p => ({ ...p, serialNumber: p.supplierCode, stock: Math.floor(Math.random() * 500) + 50 })); }
  });
  const [editingProduct, setEditingProduct] = useState<ManagedProduct | null>(null);
  const [showProductForm, setShowProductForm] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);

  const saveProducts = (products: ManagedProduct[]) => {
    setManagedProducts(products);
    localStorage.setItem('gevel_managed_products', JSON.stringify(products));
  };

  const handleSaveProduct = (p: ManagedProduct) => {
    if (editingProduct) {
      saveProducts(managedProducts.map(mp => mp.id === p.id ? p : mp));
    } else {
      saveProducts([...managedProducts, p]);
    }
    setShowProductForm(false);
    setEditingProduct(null);
  };

  const handleDeleteProduct = (id: string) => {
    saveProducts(managedProducts.filter(p => p.id !== id));
  };

  if (!user) return <Navigate to="/login" replace />;

  const savedQuotes = getQuotesForUser(user.id);
  // Force re-render trick via quotesRefresh
  void quotesRefresh;

  const handleDeleteQuote = (id: string) => {
    deleteQuote(id);
    setQuotesRefresh(prev => prev + 1);
  };

  return (
    <div className="pt-20 md:pt-24">
      <SEOHead page="dashboard" />
      <section className="bg-[#1a1a1a] text-white section-padding !pb-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-[#c47a3a] rounded-full flex items-center justify-center text-xl font-bold">G</div>
            <div>
              <h1 className="text-2xl font-bold">{user.name}</h1>
              <p className="text-gray-400 text-sm">
                {isAdmin ? t('dash.admin') : isWholesaler ? `${t('dash.wholesaler')} — ${user.company}` : t('dash.customer')}
                {isWholesaler && user.discountTier && <span className="ml-2 text-[#c47a3a]">{t('data.tier')} {user.discountTier}</span>}
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="bg-white border-b border-gray-100 sticky top-16 md:top-20 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {[
            ['overview', t('dash.overview')],
            ...(isAdmin ? [['products', t('dash.productMgmt')], ['customers', t('dash.customers')], ['orders', t('dash.orders')], ['suppliers', t('dash.suppliers')], ['analytics', t('dash.analytics')]] : []),
            ...(isWholesaler ? [['prices', t('dash.myPrices')], ['orders', t('dash.orders')]] : []),
            ...(!isAdmin && !isWholesaler ? [['quotes', t('dash.myQuotes')]] : []),
          ].map(([key, label]) => (
            <button key={key} onClick={() => setActiveTab(key)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${activeTab === key ? 'border-[#c47a3a] text-[#c47a3a]' : 'border-transparent text-[#8a7e74] hover:text-[#1a1a1a]'}`}>
              {label}
            </button>
          ))}
        </div>
      </section>

      <section className="section-padding bg-[#faf7f3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {activeTab === 'overview' && (
            <div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                {(isAdmin ? [
                  { label: t('dash.totalQuotes'), value: '127', change: '+12%', icon: FileText },
                  { label: t('dash.activeWholesalers'), value: '18', change: '+3', icon: Factory },
                  { label: t('dash.revenueMonth'), value: '€47.230', change: '+24%', icon: DollarSign },
                  { label: t('dash.conversionRate'), value: '34%', change: '+5%', icon: TrendingUp },
                ] : isWholesaler ? [
                  { label: t('dash.myOrders'), value: '23', change: '+4', icon: Package },
                  { label: t('dash.openQuotes'), value: '5', change: '', icon: FileText },
                  { label: t('dash.myDiscount'), value: `${t('data.tier')} ${user.discountTier}`, change: '', icon: Tag },
                  { label: t('dash.totalOrdered'), value: '€12.450', change: '+18%', icon: DollarSign },
                ] : [
                  { label: t('dash.myQuotes'), value: String(savedQuotes.length), change: '', icon: FileText },
                  { label: t('dash.status'), value: savedQuotes.length > 0 ? t('dash.active') : t('dash.inProgress'), change: '', icon: Clock },
                  { label: t('dash.calculations'), value: '7', change: '', icon: Calculator },
                  { label: t('dash.favorites'), value: '4', change: '', icon: Heart },
                ]).map(stat => (
                  <div key={stat.label} className="bg-white rounded-lg p-5 border border-gray-100 shadow-sm">
                    <div className="flex items-start justify-between">
                      <stat.icon size={24} className="text-[#c47a3a]" />
                      {stat.change && <span className="text-xs text-green-600 font-medium">{stat.change}</span>}
                    </div>
                    <div className="mt-3">
                      <p className="text-2xl font-bold">{stat.value}</p>
                      <p className="text-xs text-[#8a7e74] mt-0.5">{stat.label}</p>
                    </div>
                  </div>
                ))}
              </div>

              {isAdmin && (
                <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
                  <h3 className="text-lg font-bold mb-4">{t('dash.recentQuotes')}</h3>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-gray-200">
                        <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.client')}</th>
                        <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.type')}</th>
                        <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.product')}</th>
                        <th className="text-left py-2 text-[#8a7e74] font-medium">m²</th>
                        <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.status')}</th>
                      </tr></thead>
                      <tbody>
                        {[
                          { name: 'Jan Peeters', type: t('data.type.particulier'), product: 'Roma Classico', area: '120', status: t('dash.new') },
                          { name: 'Bouwcentrum NV', type: t('data.type.groothandel'), product: 'Milano Bianco', area: '500', status: t('dash.inProgress') },
                          { name: 'Studio Arcada', type: t('data.type.architect'), product: 'Antwerp Noir', area: '250', status: t('dash.quoteSent') },
                        ].map((row, i) => (
                          <tr key={i} className="border-b border-gray-50 hover:bg-[#faf7f3]">
                            <td className="py-3 font-medium">{row.name}</td>
                            <td className="py-3 text-[#8a7e74]">{row.type}</td>
                            <td className="py-3">{row.product}</td>
                            <td className="py-3">{row.area}</td>
                            <td className="py-3">
                              <span className={`px-2 py-0.5 rounded text-xs font-medium ${row.status === t('dash.new') ? 'bg-blue-50 text-blue-600' : row.status === t('dash.inProgress') ? 'bg-yellow-50 text-yellow-600' : 'bg-green-50 text-green-600'}`}>
                                {row.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'products' && isAdmin && (
            <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-lg font-bold">{t('dash.productMgmt')}</h3>
                  <p className="text-xs text-[#8a7e74] mt-0.5">{managedProducts.length} {t('nav.products').toLowerCase()}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setShowUploadModal(true)} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium border border-gray-200 rounded-lg hover:border-[#c47a3a] hover:text-[#c47a3a] transition-colors">
                    <Upload size={14} /> {t('dash.importProducts')}
                  </button>
                  <button onClick={() => { setEditingProduct(null); setShowProductForm(true); }} className="btn-primary !py-2 !px-5 text-sm flex items-center gap-1.5">
                    <Plus size={14} /> {t('dash.newProduct')}
                  </button>
                </div>
              </div>
              <div className="space-y-3">
                {managedProducts.map(p => (
                  <div key={p.id} className="flex items-center gap-4 p-4 bg-[#faf7f3] rounded-lg">
                    <img src={p.images[0]} alt={p.name} className="w-14 h-14 rounded object-cover" onError={e => { (e.target as HTMLImageElement).src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="56" height="56" fill="%23e5e7eb"><rect width="56" height="56"/><text x="28" y="32" text-anchor="middle" fill="%239ca3af" font-size="12">📦</text></svg>'; }} />
                    <div className="flex-1 min-w-0">
                      <h4 className="font-semibold">{p.name}</h4>
                      <p className="text-xs text-[#8a7e74]">
                        {p.serialNumber || p.supplierCode} • {translateSupplier(p.supplier, t)}
                        {p.stock !== undefined && <span className="ml-2">• {t('dash.stock')}: {p.stock}</span>}
                      </p>
                    </div>
                    <div className="text-right hidden sm:block">
                      <p className="font-bold text-[#c47a3a]">€{p.pricePerM2}/m²</p>
                      <p className="text-xs text-[#8a7e74]">B2B: €{p.wholesalePrice[1]}-€{p.wholesalePrice[3]}</p>
                    </div>
                    <div className="flex gap-2">
                      <button onClick={() => { setEditingProduct(p); setShowProductForm(true); }}
                        className="px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:border-[#c47a3a] hover:text-[#c47a3a] transition-colors">{t('dash.edit')}</button>
                      <button onClick={() => handleDeleteProduct(p.id)}
                        className="px-3 py-1.5 text-xs bg-white border border-gray-200 rounded-lg hover:border-red-400 hover:text-red-500 transition-colors">{t('dash.delete')}</button>
                    </div>
                  </div>
                ))}
              </div>

              {showProductForm && (
                <ProductFormModal
                  product={editingProduct}
                  onSave={handleSaveProduct}
                  onClose={() => { setShowProductForm(false); setEditingProduct(null); }}
                  t={t}
                />
              )}

              {/* Upload Modal */}
              {showUploadModal && (
                <div className="fixed inset-0 z-50 flex items-center justify-center p-4" onClick={() => setShowUploadModal(false)}>
                  <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
                  <div className="relative bg-white rounded-2xl w-full max-w-lg shadow-2xl" onClick={e => e.stopPropagation()}>
                    <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                      <h3 className="text-lg font-bold">{t('dash.importProducts')}</h3>
                      <button onClick={() => setShowUploadModal(false)} className="p-1.5 rounded-full hover:bg-gray-100"><X size={18} /></button>
                    </div>
                    <div className="p-6">
                      <div className="border-2 border-dashed border-gray-200 rounded-xl p-8 text-center hover:border-[#c47a3a] transition-colors">
                        <Upload size={40} className="text-[#8a7e74] mx-auto mb-3" />
                        <p className="text-sm font-medium mb-1">{t('dash.dragDrop')}</p>
                        <p className="text-xs text-[#8a7e74] mb-4">{t('dash.supportedFormats')}</p>
                        <label className="btn-primary !py-2.5 !px-6 text-sm cursor-pointer inline-block">
                          {t('dash.chooseFile')}
                          <input type="file" accept=".xlsx,.xls,.csv,.pdf" className="hidden" onChange={() => setShowUploadModal(false)} />
                        </label>
                      </div>
                      <div className="mt-4 p-3 bg-[#faf7f3] rounded-lg">
                        <p className="text-xs text-[#8a7e74]">
                          <strong>{t('dash.templateInfo')}</strong>: {t('dash.templateDesc')}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeTab === 'suppliers' && isAdmin && (
            <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
              <h3 className="text-lg font-bold mb-6">{t('dash.supplierMgmt')}</h3>
              <div className="space-y-4">
                {[
                  { name: translateSupplier('Fabrikant Polen', t), country: '🇵🇱', products: 6, status: t('dash.active'), exclusivity: t('dash.exclusiveContract') },
                  { name: translateSupplier('Fabrikant Spanje', t), country: '🇪🇸', products: 0, status: t('dash.inTalks'), exclusivity: t('dash.potential') },
                  { name: translateSupplier('Fabrikant Italië', t), country: '🇮🇹', products: 0, status: t('dash.planned'), exclusivity: t('dash.future') },
                ].map(s => (
                  <div key={s.name} className="flex items-center gap-4 p-4 bg-[#faf7f3] rounded-lg">
                    <span className="w-10 h-10 rounded-lg bg-[#c47a3a]/10 flex items-center justify-center text-xl">{s.country}</span>
                    <div className="flex-1">
                      <h4 className="font-semibold">{s.name}</h4>
                      <p className="text-xs text-[#8a7e74]">{s.products} {t('nav.products').toLowerCase()} • {s.exclusivity}</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-xs font-medium ${s.status === t('dash.active') ? 'bg-green-50 text-green-600' : s.status === t('dash.inTalks') ? 'bg-yellow-50 text-yellow-600' : 'bg-gray-100 text-gray-500'}`}>
                      {s.status}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'prices' && isWholesaler && (
            <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
              <h3 className="text-lg font-bold mb-2">{t('dash.yourPrices')}</h3>
              <p className="text-sm text-[#8a7e74] mb-6">{t('data.tier')} {user.discountTier} — {user.company}</p>
              <div className="space-y-3">
                {PRODUCTS.map(p => (
                  <div key={p.id} className="flex items-center gap-4 p-4 bg-[#faf7f3] rounded-lg">
                    <img src={p.images[0]} alt={p.name} className="w-12 h-12 rounded object-cover" />
                    <div className="flex-1"><h4 className="font-semibold">{p.name}</h4></div>
                    <div className="text-right">
                      <p className="text-xs text-[#8a7e74] line-through">€{p.pricePerM2}/m²</p>
                      <p className="font-bold text-[#c47a3a]">€{user.discountTier ? p.wholesalePrice[user.discountTier] : p.pricePerM2}/m²</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeTab === 'quotes' && !isAdmin && !isWholesaler && (
            <div>
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold">{t('dash.myQuotes')}</h3>
                <Link to="/calculator" className="btn-primary !py-2 !px-5 text-sm">
                  + {t('dash.newQuote')}
                </Link>
              </div>

              {savedQuotes.length === 0 ? (
                <div className="bg-white rounded-lg p-12 border border-gray-100 shadow-sm text-center">
                  <div className="w-16 h-16 rounded-2xl bg-[#c47a3a]/10 flex items-center justify-center mb-4 mx-auto"><FileText size={32} className="text-[#c47a3a]" /></div>
                  <h3 className="text-xl font-bold mb-2">{t('dash.noQuotes')}</h3>
                  <p className="text-[#8a7e74] mb-6">{t('dash.noQuotesDesc')}</p>
                  <Link to="/calculator" className="btn-primary">{t('dash.goToCalc')}</Link>
                </div>
              ) : (
                <div className="space-y-4">
                  {savedQuotes.map((quote: SavedQuote) => {
                    const d = quote.calcData;
                    const date = new Date(quote.createdAt).toLocaleDateString('nl-BE', { year: 'numeric', month: 'short', day: 'numeric' });
                    return (
                      <div key={quote.id} className="bg-white rounded-lg border border-gray-100 shadow-sm overflow-hidden">
                        <div className="p-6">
                          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-3 mb-2">
                                <h4 className="font-bold text-lg truncate">{d.productName}</h4>
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-[#c47a3a]/10 text-[#c47a3a] whitespace-nowrap">
                                  €{Math.round(d.totalPrice).toLocaleString('nl-BE')}
                                </span>
                              </div>
                              <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-[#8a7e74]">
                                <span>{d.area} m²</span>
                                <span>{d.totalStones} {t('calc.pieces')}</span>
                                {d.totalCornerPieces > 0 && <span>{d.totalCornerPieces} {t('dash.corners')}</span>}
                                {d.includeGlue && <span>{d.glueBags} {t('dash.glueBags')}</span>}
                                {d.includeGrout && <span>{d.groutBags} {t('dash.groutBags')}</span>}
                              </div>
                              <p className="text-xs text-[#8a7e74] mt-2">{date}</p>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <button
                                onClick={() => generateQuotePDF(quote)}
                                className="flex items-center gap-1.5 px-4 py-2 bg-[#c47a3a] text-white text-sm font-medium rounded-sm hover:bg-[#b06a2e] transition-colors"
                              >
                                <FileDown size={14} /> {t('dash.downloadPdf')}
                              </button>
                              <button
                                onClick={() => handleDeleteQuote(quote.id)}
                                className="px-3 py-2 text-sm bg-white border border-gray-200 rounded-sm hover:border-red-400 hover:text-red-500 transition-colors"
                                title={t('dash.delete')}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}

          {activeTab === 'customers' && isAdmin && (
            <div className="bg-white rounded-lg p-6 border border-gray-100 shadow-sm">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-bold">{t('dash.customers')}</h3>
                <div className="flex gap-2">
                  <input type="text" placeholder={t('dash.searchCustomer')} className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-[#c47a3a]" />
                </div>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200">
                      <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.client')}</th>
                      <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.type')}</th>
                      <th className="text-left py-2 text-[#8a7e74] font-medium">Email</th>
                      <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.phone')}</th>
                      <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.address')}</th>
                      <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.status')}</th>
                      <th className="text-left py-2 text-[#8a7e74] font-medium">{t('dash.totalOrdered')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {[
                      { name: 'Jan Peeters', type: t('data.type.particulier'), email: 'jan.peeters@telenet.be', phone: '+32 476 12 34 56', address: 'Kerkstraat 42, 2000 Antwerpen', status: t('dash.active'), total: '€3.240' },
                      { name: 'Bouwcentrum NV', type: t('data.type.groothandel'), email: 'info@bouwcentrum.be', phone: '+32 3 234 56 78', address: 'Industrieweg 15, 2100 Deurne', status: t('dash.active'), total: '€47.850' },
                      { name: 'Studio Arcada', type: t('data.type.architect'), email: 'contact@studioarcada.be', phone: '+32 3 345 67 89', address: 'Meir 88, 2000 Antwerpen', status: t('dash.active'), total: '€12.600' },
                      { name: 'Marie Vandenberghe', type: t('data.type.particulier'), email: 'm.vandenberghe@gmail.com', phone: '+32 486 23 45 67', address: 'Leopoldlaan 7, 9000 Gent', status: t('dash.active'), total: '€1.890' },
                      { name: 'De Nijs Bouwmaterialen', type: t('data.type.groothandel'), email: 'verkoop@denijs.be', phone: '+32 2 456 78 90', address: 'Havenstraat 33, 1000 Brussel', status: t('dash.active'), total: '€28.400' },
                      { name: 'Architectenbureau Claes', type: t('data.type.architect'), email: 'info@claes-architecten.be', phone: '+32 16 34 56 78', address: 'Bondgenotenlaan 12, 3000 Leuven', status: t('dash.inProgress'), total: '€8.750' },
                      { name: 'Thomas De Ridder', type: t('data.type.particulier'), email: 't.deridder@outlook.be', phone: '+32 498 34 56 78', address: 'Stationsstraat 19, 2800 Mechelen', status: t('dash.active'), total: '€2.150' },
                      { name: 'Wouters & Zonen BVBA', type: t('data.type.groothandel'), email: 'info@wouters-zonen.be', phone: '+32 3 567 89 01', address: 'Bredabaan 200, 2170 Merksem', status: t('dash.inProgress'), total: '€15.300' },
                      { name: 'Lien Janssens', type: t('data.type.particulier'), email: 'lien.janssens@hotmail.com', phone: '+32 479 45 67 89', address: 'Groenplaats 5, 2000 Antwerpen', status: t('dash.active'), total: '€4.680' },
                      { name: 'Van Hoeck Constructies', type: t('data.type.architect'), email: 'projects@vanhoeck.be', phone: '+32 3 678 90 12', address: 'Turnhoutsebaan 45, 2140 Borgerhout', status: t('dash.active'), total: '€21.200' },
                    ].map((row, i) => (
                      <tr key={i} className="border-b border-gray-50 hover:bg-[#faf7f3] cursor-pointer">
                        <td className="py-3 font-medium">{row.name}</td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            row.type === t('data.type.groothandel') ? 'bg-purple-50 text-purple-600' :
                            row.type === t('data.type.architect') ? 'bg-blue-50 text-blue-600' :
                            'bg-gray-100 text-gray-600'
                          }`}>{row.type}</span>
                        </td>
                        <td className="py-3 text-[#8a7e74]">{row.email}</td>
                        <td className="py-3 text-[#8a7e74]">{row.phone}</td>
                        <td className="py-3 text-[#8a7e74] text-xs">{row.address}</td>
                        <td className="py-3">
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${row.status === t('dash.active') ? 'bg-green-50 text-green-600' : 'bg-yellow-50 text-yellow-600'}`}>
                            {row.status}
                          </span>
                        </td>
                        <td className="py-3 font-medium">{row.total}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {(activeTab === 'orders' || activeTab === 'analytics') && (
            <div className="bg-white rounded-lg p-12 border border-gray-100 shadow-sm text-center">
              <div className="w-16 h-16 rounded-2xl bg-[#c47a3a]/10 flex items-center justify-center mb-4 mx-auto"><Construction size={32} className="text-[#c47a3a]" /></div>
              <h3 className="text-xl font-bold mb-2">{t('dash.comingSoon')}</h3>
              <p className="text-[#8a7e74]">{t('dash.comingSoonDesc')}</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
